import { useCallback, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Spin, Form, Input, Button, Row, Col, Select, Slider, Checkbox, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";

import ContentCard from "../../../components/ContentCard/ContentCard";
import DefaultCardHeader from "../../../components/DefaultCardHeader/DefaultCardHeader";
import { Categories, SubmissionStatus } from "../../../common/utils";

const { Option } = Select;

export default function UpdateAmbassadorPage() {
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropTouched, setCropTouched] = useState(false);
  const [cropInitialized, setCropInitialized] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);

  const onCropComplete = useCallback((croppedArea, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleCropChange = (newCrop) => {
    setCrop(newCrop);
    setCropTouched(true);
  };

  const handleZoomChange = (newZoom) => {
    setZoom(newZoom);
    setCropTouched(true);
  };

  const handleImageReupload = (file) => {
    setNewImageFile(file);
    setNewImagePreview(URL.createObjectURL(file));
    // Reset crop state for the new image
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropTouched(false);
    return false; // Prevent auto upload
  };

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const history = useHistory();
  const location = useLocation();

  const fetchApplicationById = async () => {
    setLoading(true);
    try {
      if (location && location.pathname && location.pathname !== "") {
        const id = location.pathname.split("/")[2];

        const response = await fetch(
          `${process.env.REACT_APP_API_MASTER}/team-application/${id}`
        );
        const data = await response.json();

        if (data.success) {
          setApplicationData(data.data);
        }
      }
    } catch {
      // fetch error
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchApplicationById();
  }, []);

  // Restore saved crop state when application data loads
  useEffect(() => {
    if (applicationData && !cropInitialized) {
      setCrop({
        x: applicationData.cropX || 0,
        y: applicationData.cropY || 0,
      });
      setZoom(applicationData.cropZoom || 1);
      setCropInitialized(true);
    }
  }, [applicationData]);

  const onFinish = async (values) => {
    setUpdating(true);
    try {
      if (location && location.pathname && location.pathname !== "") {
        const id = location.pathname.split("/")[2];

        const formData = new FormData();
        formData.append("fullName", values.fullName);
        formData.append("emailAddress", values.emailAddress || "");
        formData.append("phone", values.phone || "");
        formData.append("position", values.position || "");
        formData.append("bio", values.bio || "");
        formData.append("status", values.status || "");
        formData.append("category", values.category || "");
        formData.append("isCurrentMember", values.isCurrentMember ? "1" : "0");
        formData.append("cropX", crop.x);
        formData.append("cropY", crop.y);
        formData.append("cropZoom", zoom);

        if (newImageFile) {
          formData.append("image", newImageFile);
        }
        if (cropTouched && croppedAreaPixels) {
          formData.append("cropData", JSON.stringify(croppedAreaPixels));
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_MASTER}/team-application/${id}`,
          {
            method: "PUT",
            body: formData,
          }
        );
        const data = await response.json();

        if (data.success) {
          messageApi.open({
            type: "success",
            content: "Application updated successfully",
          });
        } else {
          messageApi.open({
            type: "error",
            content: data.message,
          });
        }
      }
    } catch (err) {
      messageApi.open({
        type: "error",
        content: "Failed to update application",
      });
    }
    setUpdating(false);
  };

  return (
    <>
      {contextHolder}
      <ContentCard
        isStickyHead
        headerContent={
          <div>
            <DefaultCardHeader>
              <div className="ambassadors-card__header">Update Submission</div>
            </DefaultCardHeader>
          </div>
        }
      >
        {loading && <Spin />}
        {!loading && (applicationData === null || !applicationData) && (
          <h3>No submission found!!!</h3>
        )}
        {applicationData && (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              fullName: applicationData.fullName,
              emailAddress: applicationData.emailAddress,
              phone: applicationData.phone,
              position: applicationData.position,
              bio: applicationData.bio,
              status: applicationData.status,
              category: applicationData.category,
              isCurrentMember: applicationData.isCurrentMember,
            }}
          >
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 8 }}>
                Profile Image
              </label>
              {(newImagePreview || applicationData.originalProfile || applicationData.profile) && (
                <>
                  <div
                    style={{
                      position: "relative",
                      width: 300,
                      height: 300,
                      background: "#333",
                      borderRadius: 8,
                    }}
                  >
                    <Cropper
                      image={newImagePreview || applicationData.originalProfile || applicationData.profile}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={handleCropChange}
                      onZoomChange={handleZoomChange}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div style={{ width: 300, marginTop: 12 }}>
                    <label style={{ fontSize: 13, color: "#666" }}>Zoom</label>
                    <Slider
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={handleZoomChange}
                    />
                  </div>
                </>
              )}
              <Upload
                beforeUpload={handleImageReupload}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} style={{ marginTop: 12 }}>
                  Re-upload Image
                </Button>
              </Upload>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Full Name"
                  name="fullName"
                  rules={[
                    { required: true, message: "Please input your full name!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Email Address" name="emailAddress">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Phone" name="phone">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Position"
                  name="position"
                  rules={[
                    { required: true, message: "Please input your position!" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[
                    { required: true, message: "Please select a status!" },
                  ]}
                >
                  <Select placeholder="Select a status">
                    <Option value={SubmissionStatus.PENDING}>PENDING</Option>
                    <Option value={SubmissionStatus.REJECTED}>REJECTED</Option>
                    <Option value={SubmissionStatus.ACCEPTED}>ACCEPTED</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Category" name="category">
                  <Select placeholder="Select a category">
                    <Option value={Categories.THE_BOARD_SECTION}>
                      The Board Section
                    </Option>
                    <Option value={Categories.ADVISORY_BOARD}>
                      Advisory Board
                    </Option>
                    <Option value={Categories.HEADS_OF_RESEARCH}>
                      Operations
                    </Option>
                    <Option value={Categories.FIELD_RESEARCH_OFFICERS}>
                      Field Research Officers
                    </Option>
                    <Option value={Categories.ADVOCATES}>
                      Advocates
                    </Option>
                    <Option value={Categories.BRAND_AMBASSADORS}>
                      Ambassadors
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="isCurrentMember"
              valuePropName="checked"
            >
              <Checkbox>Current Member</Checkbox>
            </Form.Item>

            <Form.Item
              label="Bio"
              name="bio"
              rules={[{ required: true, message: "Please input your bio!" }]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                disabled={updating}
                loading={updating}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        )}
      </ContentCard>
    </>
  );
}
